import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

import {
  type PieceBackfillRunFieldsFragment,
  PieceBackfillRunMode,
  PieceBackfillRunStatus,
  usePieceBackfillRunsQuery,
  usePieceBackfillSurveyLazyQuery,
} from "../../generated/graphql";
import {
  buildRunSignature,
  isExecuteUnlocked,
  signatureFromRun,
} from "./piece-backfill-confirmation";
import { useCrownEnv } from "../crown-data-import/env/env-context";
import PieceBackfillExecuteDialog from "./piece-backfill-execute-dialog";
import PieceBackfillHistory from "./piece-backfill-history";
import { parsePieceBackfillReport } from "./piece-backfill-report";
import PieceBackfillRunPanel from "./piece-backfill-run-panel";
import { isRunInFlight } from "./piece-backfill-run-state";
import PieceBackfillSetup from "./piece-backfill-setup";
import PieceBackfillSurveyPanel from "./piece-backfill-survey-panel";
import { usePieceBackfillRun } from "./use-piece-backfill-run";

/** How far back the cutoff starts, in years. Widening it is one field away. */
const DEFAULT_CUTOFF_YEARS_BACK = 1;

const styles = {
  subtitle: { mt: 0.5, mb: 3, maxWidth: 720 },
  alert: { mb: 2, maxWidth: 720 },
  actions: { mt: 4 },
  actionHint: { mt: 1 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Piece backfill tool container. Owns the run parameters, the survey, the active
 * run, and the execute gate; every panel below it is presentational.
 *
 * The gate is the point of the tool: EXECUTE only unlocks after a DRY_RUN
 * completes for the same company, cutoff, and billing-party selection, and even
 * then it needs a typed confirmation. The cleared signature is read off the
 * finished run's own stored parameters, so editing the form after a dry run
 * re-locks execute instead of silently executing something else.
 */
const PieceBackfill = () => {
  const { env } = useCrownEnv();
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [cutoffDate, setCutoffDate] = useState(() =>
    dayjs().subtract(DEFAULT_CUTOFF_YEARS_BACK, "year").format("YYYY-MM-DD"),
  );
  const [notes, setNotes] = useState("");
  const [selectedContactUuids, setSelectedContactUuids] = useState<string[]>(
    [],
  );
  const [clearedSignature, setClearedSignature] = useState<string | null>(null);
  const [clearedPieceCount, setClearedPieceCount] = useState(0);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);

  const cutoffIso = useMemo(() => {
    const parsed = dayjs(cutoffDate);
    return parsed.isValid() ? parsed.startOf("day").toISOString() : null;
  }, [cutoffDate]);

  const currentSignature = useMemo(() => {
    if (companyUuid === null || cutoffIso === null) {
      return null;
    }
    return buildRunSignature({
      env,
      companyUuid,
      cutoffDate: cutoffIso,
      billingPartyContactUuids: selectedContactUuids,
    });
  }, [env, companyUuid, cutoffIso, selectedContactUuids]);

  const [
    runSurvey,
    { data: surveyData, loading: surveyLoading, error: surveyError },
  ] = usePieceBackfillSurveyLazyQuery({ fetchPolicy: "network-only" });

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = usePieceBackfillRunsQuery({
    variables: { input: { companyUuid: companyUuid ?? "" } },
    skip: companyUuid === null,
    fetchPolicy: "cache-and-network",
  });

  const handleRunFinished = useCallback(
    (finished: PieceBackfillRunFieldsFragment) => {
      void refetchHistory();
      if (
        finished.mode !== PieceBackfillRunMode.DryRun ||
        finished.status !== PieceBackfillRunStatus.Completed
      ) {
        return;
      }
      const signature = signatureFromRun(finished, env);
      if (signature === null) {
        return;
      }
      setClearedSignature(signature);
      setClearedPieceCount(
        parsePieceBackfillReport(finished.report)?.totals.piecesToCreate ?? 0,
      );
    },
    [env, refetchHistory],
  );

  const { start, starting, startError, run, pollAbandoned } =
    usePieceBackfillRun({ onRunFinished: handleRunFinished });

  const runInFlight = run !== null && isRunInFlight(run.status);
  const busy = starting || runInFlight;

  const resetGate = useCallback(() => {
    setClearedSignature(null);
    setClearedPieceCount(0);
  }, []);

  const handleCompanyChange = useCallback(
    (nextUuid: string | null, nextName: string | null) => {
      setCompanyUuid(nextUuid);
      setCompanyName(nextName);
      setSelectedContactUuids([]);
      resetGate();
    },
    [resetGate],
  );

  const handleCutoffChange = useCallback(
    (next: string) => {
      setCutoffDate(next);
      resetGate();
    },
    [resetGate],
  );

  const handleSelectionChange = useCallback(
    (next: string[]) => {
      setSelectedContactUuids(next);
      resetGate();
    },
    [resetGate],
  );

  const handleSurvey = useCallback(() => {
    if (companyUuid === null || cutoffIso === null) {
      return;
    }
    void runSurvey({
      variables: { input: { companyUuid, cutoffDate: cutoffIso } },
    });
  }, [companyUuid, cutoffIso, runSurvey]);

  const handleDryRun = useCallback(() => {
    if (companyUuid === null || cutoffIso === null) {
      return;
    }
    resetGate();
    void start({
      companyUuid,
      cutoffDate: cutoffIso,
      billingPartyContactUuids: selectedContactUuids,
      mode: PieceBackfillRunMode.DryRun,
      notes: notes.trim().length === 0 ? null : notes.trim(),
    });
  }, [companyUuid, cutoffIso, notes, resetGate, selectedContactUuids, start]);

  const handleExecute = useCallback(() => {
    if (companyUuid === null || cutoffIso === null) {
      return;
    }
    setExecuteDialogOpen(false);
    resetGate();
    void start({
      companyUuid,
      cutoffDate: cutoffIso,
      billingPartyContactUuids: selectedContactUuids,
      mode: PieceBackfillRunMode.Execute,
      confirmExecute: true,
      notes: notes.trim().length === 0 ? null : notes.trim(),
    });
  }, [companyUuid, cutoffIso, notes, resetGate, selectedContactUuids, start]);

  const closeExecuteDialog = useCallback(() => {
    setExecuteDialogOpen(false);
  }, []);

  const openExecuteDialog = useCallback(() => {
    setExecuteDialogOpen(true);
  }, []);

  const canRun = companyUuid !== null && cutoffIso !== null && !busy;
  const executeUnlocked =
    currentSignature !== null &&
    isExecuteUnlocked({ currentSignature, clearedSignature, busy });

  return (
    <Box>
      <Typography variant="h5">Piece backfill</Typography>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Creates the missing piece rows for a company&apos;s existing packages,
        so piece-level tracking works on orders that predate it. Survey first,
        dry run second, execute last: nothing is written until you confirm an
        execute.
      </Typography>

      {startError === undefined ? null : (
        <Alert severity="error" sx={styles.alert}>
          {startError.message}
        </Alert>
      )}

      <PieceBackfillSetup
        companyUuid={companyUuid}
        cutoffDate={cutoffDate}
        notes={notes}
        disabled={busy}
        onCompanyChange={handleCompanyChange}
        onCutoffDateChange={handleCutoffChange}
        onNotesChange={setNotes}
      />

      <PieceBackfillSurveyPanel
        survey={surveyData?.pieceBackfillSurvey}
        loading={surveyLoading}
        errorMessage={surveyError?.message}
        canSurvey={companyUuid !== null && cutoffIso !== null}
        selectedContactUuids={selectedContactUuids}
        disabled={busy}
        onSurvey={handleSurvey}
        onSelectionChange={handleSelectionChange}
      />

      <Box sx={styles.actions}>
        <Typography variant="subtitle1">2. Dry run, then execute</Typography>
        <Stack direction="row" spacing={1.5} sx={styles.actionHint}>
          <Button variant="contained" disabled={!canRun} onClick={handleDryRun}>
            Start dry run
          </Button>
          <Button
            color="warning"
            variant="outlined"
            disabled={!executeUnlocked}
            onClick={openExecuteDialog}
          >
            Execute backfill
          </Button>
        </Stack>
        {executeUnlocked ? null : (
          <Typography
            color="text.secondary"
            variant="body2"
            sx={styles.actionHint}
          >
            Execute unlocks after a dry run completes for these exact
            parameters. Changing the company, cutoff, or billing-party selection
            locks it again.
          </Typography>
        )}
      </Box>

      <PieceBackfillRunPanel run={run} pollAbandoned={pollAbandoned} />

      <PieceBackfillHistory
        runs={historyData?.pieceBackfillRuns ?? []}
        loading={historyLoading}
        hasCompany={companyUuid !== null}
      />

      <PieceBackfillExecuteDialog
        open={executeDialogOpen}
        companyName={companyName ?? ""}
        plannedPieceCount={clearedPieceCount}
        submitting={starting}
        onCancel={closeExecuteDialog}
        onConfirm={handleExecute}
      />
    </Box>
  );
};

export default PieceBackfill;
